/**
 * SharedRouterTransition
 *
 * Provides animated page transitions for Next.js App Router layouts using AnimatePresence.
 *
 * - Freezes the router context during transitions to prevent React unmount/remount bugs.
 * - Uses AnimatePresence to animate route entry/exit (e.g. fade, slide, etc).
 * - Ensures only one route is mounted at a time, keyed by pathname.
 * - Wraps children in a PresenceProvider for animation lifecycle control.
 *
 * Usage:
 * Place <SharedRouterTransition> high in your layout tree (e.g. in app/layout.tsx):
 *
 * ```tsx
 * <SharedRouterTransition>
 *   {children}
 * </SharedRouterTransition>
 * ```
 *
 * See also:
 * - AnimatePresence
 * - https://nextjs.org/docs/app/building-your-application/routing/pages-and-layouts
 * - https://www.framer.com/motion/animate-presence/
 */

"use client"

import type { ContextType, ReactNode } from "react"
import { useContext, useEffect, useRef, useState } from "react"
// WARNING: LayoutRouterContext is an internal Next.js API.
// Monitor for breaking changes in Next.js updates.
// See: https://www.imcorfitz.com/posts/adding-framer-motion-page-transitions-to-next-js-app-router
import { LayoutRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime"
import { usePathname } from "next/navigation"

import { AnimatePresence } from "./AnimatePresence/AnimatePresence"

type FrozenRouterProps = {
  layoutRouterContext: ContextType<typeof LayoutRouterContext>
  children: ReactNode
}

const FrozenRouter = ({ layoutRouterContext, children }: FrozenRouterProps) => {
  const frozenLayoutRouterContextRef = useRef(layoutRouterContext)

  return (
    <LayoutRouterContext.Provider value={frozenLayoutRouterContextRef.current}>
      {children}
    </LayoutRouterContext.Provider>
  )
}

type SharedRouterTransitionProps = {
  children: ReactNode
}

export const SharedRouterTransition = ({
  children,
}: SharedRouterTransitionProps) => {
  const layoutRouterContext = useContext(LayoutRouterContext)

  const pathname = usePathname()
  const [currentPathname, setCurrentPathname] = useState(pathname)

  useEffect(() => {
    setCurrentPathname(() => {
      return `${pathname}_${Date.now()}` // append date.now to prevent duplicate keys on fast navigation
    })
  }, [pathname])

  return (
    <AnimatePresence>
      <FrozenRouter
        key={currentPathname}
        layoutRouterContext={layoutRouterContext}
      >
        {children}
      </FrozenRouter>
    </AnimatePresence>
  )
}
